import React, { useState, useRef } from "react";
import { useSiteContext } from "../../contexts/SiteContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  GlobeIcon,
  MiniArrowIcon,
  HamburgerIcon,
  CloseIcon,
} from "../../assets/icons/";
import logo from "../../assets/Logo-en.svg";
import "./Header.css";
import { getImageUrl } from "../../services/fileService";

const Header = () => {
  const { currentLanguageCode, changeLanguage } = useSiteContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const dropdownRef = useRef(null);
  const submenuRefs = useRef({});

  const {
    getSection,
    sectionData,
  } = useSiteContext();

  const headerData = getSection("header_menu");
  const menuData = headerData?.main_menu;

  // Dil bazlı URL yönlendirme haritası
  const routeMapping = {
    "/tarihce": "/history",
    "/history": "/tarihce",
    "/politikalar": "/policies",
    "/policies": "/politikalar",
    "/misyon-vizyon-degerler": "/mission-vision-values",
    "/mission-vision-values": "/misyon-vizyon-degerler",
    "/tarifeli-ucus": "/scheduled-flights",
    "/scheduled-flights": "/tarifeli-ucus",
    "/charter-ucus": "/charter-flights",
    "/charter-flights": "/charter-ucus",
    "/yer-hizmetleri": "/ground-handling",
    "/ground-handling": "/yer-hizmetleri",
    "/kariyer": "/careers",
    "/careers": "/kariyer",
    "/antrepo": "/warehouse",
    "/warehouse": "/antrepo",
    "/teknik-hizmetler": "/technical-services",
    "/technical-services": "/teknik-hizmetler",
    "/canli-hayvan-tasimaciligi": "/live-animals",
    "/live-animals": "/canli-hayvan-tasimaciligi",
    "/tehlikeli-maddeler": "/dangerous-goods",
    "/dangerous-goods": "/tehlikeli-maddeler",
    "/haberler": "/news",
    "/news": "/haberler",
    "/karayolu-tasimaciligi": "/truck-services",
    "/truck-services": "/karayolu-tasimaciligi",
    "/iletisim": "/contact",
    "/contact": "/iletisim",
    "/surdurulebilirlik": "/sustainability",
    "/sustainability": "/surdurulebilirlik",
    "/ag-ve-filo": "/network-and-fleet",
    "/network-and-fleet": "/ag-ve-filo",
    "/rezervasyon": "/book-now",
    "/book-now": "/rezervasyon",
    "/bozulabilir-ve-ilac-kargosu": "/perishable-and-pharma-cargo",
    "/perishable-and-pharma-cargo": "/bozulabilir-ve-ilac-kargosu",
    "/ilac-tasimaciligi": "/pharma-shipment",
    "/pharma-shipment": "/ilac-tasimaciligi",
    "/etik-hatti": "/ethic-hotline",
    "/ethic-hotline": "/etik-hatti",
    "/kalite-standartlari": "/quality-standards",
    "/quality-standards": "/kalite-standartlari",
    "/antrepo-test": "/warehouse-test",
    "/warehouse-test": "/antrepo-test",
    "/kariyer-test": "/career-test",
    "/career-test": "/kariyer-test",
    "/surdurulebilirlik-test": "/sustainability-test",
    "/sustainability-test": "/surdurulebilirlik-test",
  };

  const handleLanguageChange = (newCode, langName, rtl) => {
    const currentPath = location.pathname;
    let newPath = currentPath;

    // Haber detayı kontrolü
    const isNewsDetailTR = currentPath.startsWith("/haberler/");
    const isNewsDetailEN = currentPath.startsWith("/news/");

    if (isNewsDetailTR || isNewsDetailEN) {
      const currentPrefix = isNewsDetailTR ? "/haberler/" : "/news/";
      const currentSlug = currentPath.substring(currentPrefix.length);
      const targetPrefix = newCode === "tr" ? "/haberler/" : "/news/";

      // Haber indexini bul ve yeni slug'ı al
      if (sectionData) {
        const newsSections = sectionData.filter(
          (s) => s.sectionHandle === "news",
        );
        const currentLangSection = newsSections.find(
          (s) => JSON.parse(s.content || "{}").lang === currentLanguageCode,
        );
        const targetLangSection = newsSections.find(
          (s) => JSON.parse(s.content || "{}").lang === newCode,
        );

        if (currentLangSection && targetLangSection) {
          const currentItems =
            JSON.parse(currentLangSection.content).data?.news_details || [];
          const targetItems =
            JSON.parse(targetLangSection.content).data?.news_details || [];

          const index = currentItems.findIndex(
            (i) =>
              i.news_title.replace(/\s+/g, "-").toLowerCase() === currentSlug,
          );

          if (index !== -1 && targetItems[index]) {
            const newSlug = targetItems[index].news_title
              .replace(/\s+/g, "-")
              .toLowerCase();
            newPath = targetPrefix + newSlug;
          } else {
            newPath = targetPrefix + currentSlug;
          }
        } else {
          newPath = targetPrefix + currentSlug;
        }
      } else {
        newPath = targetPrefix + currentSlug;
      }
    } else {
      // Normal eşleşmeler
      newPath = routeMapping[currentPath] || currentPath;
    }

    changeLanguage(newCode, langName, rtl);
    if (newPath !== currentPath) {
      navigate(newPath);
    }
  };

  // Kapanma için tıklama dinleyicisi
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }

      // Submenu için click outside kontrolü
      let clickedInAnySubmenu = false;
      let clickedOnMenuItem = false;

      // Submenu içindeki tıklamalar
      Object.values(submenuRefs.current).forEach((ref) => {
        if (ref && ref.contains(event.target)) {
          clickedInAnySubmenu = true;
        }
      });

      // Menu item'larındaki tıklamalar (ana menü linklerinde)
      const menuLinks = document.querySelectorAll(".menu-link");
      menuLinks.forEach((link) => {
        if (link.contains(event.target)) {
          clickedOnMenuItem = true;
        }
      });

      // Eğer submenu dışında ve menu item dışında tıklandıysa kapat
      if (!clickedInAnySubmenu && !clickedOnMenuItem) {
        setActiveSubmenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Menü açıkken arka planın scroll olmasını engelle
  React.useEffect(() => {
    if (sideMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [sideMenuOpen]);

  const menuItems = menuData || [];

  return (
    <header className="header">
      <nav className="container-fluid d-flex align-items-center justify-content-between nav-mng">
        <a href="/">
          <img
            src={getImageUrl(headerData?.logo)}
            className="img-fluid"
            alt="Mng Airlines"
            width="200"
            height="80"
          />
        </a>
        <div className="nav-right">
          <ul className="menu-ul">
            {menuItems.map((item, idx) => (
              <li
                key={idx}
                className={
                  item.submenus && item.submenus.length > 0
                    ? "menu-item-dropdown"
                    : ""
                }
                style={{ position: "relative" }}
                ref={
                  item.submenus && item.submenus.length > 0
                    ? (el) => (submenuRefs.current[idx] = el)
                    : null
                }
                onMouseEnter={() => {
                  if (item.submenus && item.submenus.length > 0) {
                    setActiveSubmenu(idx);
                  }
                }}
                onMouseLeave={(e) => {
                  const currentSubmenu = submenuRefs.current[idx];
                  const toElement = e.relatedTarget;

                  // Mouse submenu'nun içine gidiyorsa KAPATMA
                  if (currentSubmenu && currentSubmenu.contains(toElement)) {
                    return;
                  }

                  setActiveSubmenu(null);
                }}
              >
                <a
                  href={
                    item.menu_url
                      ? item.menu_url.startsWith("http")
                        ? item.menu_url
                        : item.menu_url.startsWith("/")
                          ? item.menu_url
                          : "/" + item.menu_url
                      : "#"
                  }
                  className="menu-link"
                >
                  {item.menu_name}
                  {item.submenus && item.submenus.length > 0 && (
                    <MiniArrowIcon
                      style={{
                        marginLeft: 4,
                        width: 24,
                        height: 24,
                        rotate: "90deg",
                        color: "#525866",
                      }}
                    />
                  )}
                </a>
                {item.submenus &&
                  item.submenus.length > 0 &&
                  activeSubmenu === idx && (
                    <>
                      <div className="submenu-hover-catcher" />
                      <ul className="submenu-ul">
                        {item.submenus.map((sub, i) => {
                          const url =
                            sub.submenu_url || sub.menu_url || sub.url || "#";
                          const finalUrl =
                            url.startsWith("http") || url === "#"
                              ? url
                              : url.startsWith("/")
                                ? url
                                : "/" + url;
                          return (
                            <li key={i}>
                              <a href={finalUrl} className="submenu-link">
                                {sub.submanu_name || sub.menu_name || sub.title}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
              </li>
            ))}
          </ul>

          <div className="nav-buttons">
            <div
              className="custom-dropdown"
              ref={dropdownRef}
              style={{ position: "relative", marginLeft: 24 }}
            >
              <button
                className="dropdown-toggle-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 12px",
                  borderRadius: 8,
                  border: "none",
                  fontSize: 15,
                  background: "#fff",
                  cursor: "pointer",
                  opacity: 1,
                }}
              >
                <GlobeIcon
                  style={{ width: 20, height: 20, color: "#99A0AE" }}
                />
                <span
                  style={{
                    color: "#525866",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  {currentLanguageCode === "tr" ? "TR" : "EN"}
                </span>

                <MiniArrowIcon
                  style={{
                    width: 20,
                    height: 20,
                    color: "#525866",
                    rotate: dropdownOpen ? "180deg" : "90deg",
                    transition: "rotate 0.3s ease",
                  }}
                />
              </button>
              {dropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    background: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    minWidth: 120,
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    marginTop: 8,
                    zIndex: 1000,
                  }}
                >
                  <button
                    onClick={() => {
                      handleLanguageChange("tr", "Türkçe", false);
                      setDropdownOpen(false);
                    }}
                    disabled={currentLanguageCode === "tr"}
                    className="lang-dropdown-button lang-dropdown-button-first"
                  >
                    Türkçe (TR)
                  </button>
                  <button
                    onClick={() => {
                      handleLanguageChange("en", "English", false);
                      setDropdownOpen(false);
                    }}
                    disabled={currentLanguageCode === "en"}
                    className="lang-dropdown-button"
                  >
                    English (EN)
                  </button>
                </div>
              )}
            </div>
            <button
              className="hamburger-box"
              onClick={() => setSideMenuOpen(true)}
            >
              <HamburgerIcon
                style={{ width: 20, height: 20, color: "#99A0AE" }}
              />
            </button>
          </div>
        </div>
        {/* Side Menu Overlay */}
        {sideMenuOpen && (
          <div
            className="side-menu-overlay"
            onClick={() => setSideMenuOpen(false)}
          />
        )}
        {/* Side Menu */}
        <div className={`side-menu${sideMenuOpen ? " open" : ""}`}>
          <div className="side-menu-header">
            <a href="/">
              <img src={getImageUrl(headerData?.logo)} className="side-menu-logo" alt="Mng Airlines" />
            </a>
            <button
              className="side-menu-close"
              onClick={() => setSideMenuOpen(false)}
            >
              <CloseIcon style={{ width: 20, height: 20, color: "#525866" }} />
            </button>
          </div>

          <div className="side-menu-lang-selector" style={{ padding: '0', borderBottom: '1px solid #E1E4EA' }}>
            <div 
              onClick={() => setExpandedSection(expandedSection === 'lang' ? null : 'lang')}
              style={{
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '20px 24px',
                cursor: 'pointer',
                background: expandedSection === 'lang' ? '#F9FAFB' : 'transparent',
                transition: 'background 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <GlobeIcon style={{ width: 22, height: 22, color: '#00ADEE' }} />
                <span style={{ fontSize: '16px', color: '#011927', fontWeight: '500' }}>
                  {currentLanguageCode === 'tr' ? 'Türkçe' : 'English'}
                </span>
              </div>
              <MiniArrowIcon
                style={{
                  width: 20,
                  height: 20,
                  color: '#525866',
                  rotate: expandedSection === 'lang' ? '180deg' : '90deg',
                  transition: 'rotate 0.3s ease'
                }}
              />
            </div>
            
            {/* Dropdown Content */}
            {expandedSection === 'lang' && (
              <div style={{ padding: '0 24px 20px 56px', background: '#F9FAFB', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <button
                  onClick={() => {
                    handleLanguageChange("tr", "Türkçe", false);
                    setSideMenuOpen(false);
                    setExpandedSection(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: currentLanguageCode === 'tr' ? '#00ADEE' : '#525866',
                    fontSize: '15px',
                    fontWeight: currentLanguageCode === 'tr' ? '600' : '400'
                  }}
                >
                  <span>Türkçe (TR)</span>
                  {currentLanguageCode === 'tr' && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00ADEE' }} />}
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange("en", "English", false);
                    setSideMenuOpen(false);
                    setExpandedSection(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: currentLanguageCode === 'en' ? '#00ADEE' : '#525866',
                    fontSize: '15px',
                    fontWeight: currentLanguageCode === 'en' ? '600' : '400'
                  }}
                >
                  <span>English (EN)</span>
                  {currentLanguageCode === 'en' && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00ADEE' }} />}
                </button>
              </div>
            )}
          </div>
          <div className="side-menu-content">
            {menuItems.map((item, idx) => (
              <div key={idx} className="side-menu-section">
                {item.submenus && item.submenus.length > 0 ? (
                  <>
                    <div
                      className="side-menu-section-header"
                      onClick={() =>
                        setExpandedSection(expandedSection === idx ? null : idx)
                      }
                    >
                      <span className="side-menu-section-title">
                        {item.menu_name}
                      </span>
                      {item.submenus && (
                        <MiniArrowIcon
                          style={{
                            width: 20,
                            height: 20,
                            rotate: expandedSection === idx ? "90deg" : "0deg",
                            color: "#525866",
                            transition: "rotate 0.3s ease",
                          }}
                        />
                      )}
                    </div>
                    {expandedSection === idx && (
                      <ul className="side-menu-list">
                        {item.submenus.map((sub, i) => {
                          const isActive =
                            window.location.pathname ===
                            (sub.submenu_url || sub.menu_url || sub.url);
                          return (
                            <li
                              key={i}
                              className={
                                isActive
                                  ? "side-menu-item active"
                                  : "side-menu-item"
                              }
                            >
                              <a
                                href={
                                  sub.submenu_url ||
                                  sub.menu_url ||
                                  sub.url ||
                                  "#"
                                }
                                className="side-menu-link"
                              >
                                {sub.submanu_name || sub.menu_name || sub.title}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  <a
                    href={item.menu_url || "#"}
                    className="side-menu-direct-link"
                  >
                    {item.menu_name}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
